import jsPDF from 'jspdf';
import { Event, EventType } from '@/models/event';
import { Firestore } from 'firebase/firestore';
import { BRAND_COLOR, FONT_HELCH, PaperSize } from '../pdf-constants';
import { toAdHocPacificDate, getPacificMonthKey } from '../../date-utils';
import { fetchClubInfo, loadImage } from '../pdf-utils';

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

function hexToRgb(hex: string): [number, number, number] {
    const h = hex.replace('#', '');
    return [
        parseInt(h.substring(0, 2), 16),
        parseInt(h.substring(2, 4), 16),
        parseInt(h.substring(4, 6), 16),
    ];
}

function truncateText(doc: jsPDF, text: string, maxWidth: number): string {
    if (doc.getTextWidth(text) <= maxWidth) return text;
    let t = text;
    while (t.length > 0 && doc.getTextWidth(t + '\u2026') > maxWidth) {
        t = t.slice(0, -1);
    }
    return t + '\u2026';
}

export async function exportAnnualFormat(
    events: Event[],
    eventTypes: EventType[],
    firestore: Firestore,
    paperSize: PaperSize = 'letter',
    year?: number
) {
    const doc = new jsPDF({ format: paperSize });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const clubInfo = await fetchClubInfo(firestore);
    const scale = pageWidth / 215.9;
    const margin = 10;

    const targetYear = year || new Date().getFullYear();

    // Build event type color map
    const typeColorMap: Record<string, [number, number, number]> = {};
    eventTypes.forEach(et => {
        if (et.id && et.color) {
            typeColorMap[et.id] = hexToRgb(et.color);
        }
    });

    // Filter to target year (full year, including past) and sort
    const yearEvents = events
        .filter(e => {
            const key = getPacificMonthKey(e.date);
            return key.startsWith(String(targetYear));
        })
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Group by month
    const eventsByMonth: Record<string, Event[]> = {};
    for (let m = 1; m <= 12; m++) {
        const key = `${targetYear}-${String(m).padStart(2, '0')}`;
        eventsByMonth[key] = [];
    }
    yearEvents.forEach(e => {
        const key = getPacificMonthKey(e.date);
        if (eventsByMonth[key]) eventsByMonth[key].push(e);
    });

    // Pre-load logo
    let logoImg: HTMLImageElement | null = null;
    let logoDrawW = 0;
    let logoDrawH = 0;

    if (clubInfo?.clubLogoUrl) {
        try {
            logoImg = await loadImage(clubInfo.clubLogoUrl);
            const targetH = 12 * scale;
            const imgAspect = logoImg.naturalWidth / logoImg.naturalHeight;
            if (imgAspect > 1) {
                logoDrawW = targetH;
                logoDrawH = targetH / imgAspect;
            } else {
                logoDrawH = targetH;
                logoDrawW = targetH * imgAspect;
            }
        } catch (e) {
            console.warn("Failed to preload logo for header", e);
        }
    }

    // Header
    let topMargin = 35;

    if (logoImg) {
        const headerLogoH = 15 * scale;
        const imgAspect = logoImg.naturalWidth / logoImg.naturalHeight;
        const headerLogoW = headerLogoH * imgAspect;
        const logoX = (pageWidth - headerLogoW) / 2;
        doc.addImage(logoImg, 'PNG', logoX, 5, headerLogoW, headerLogoH);

        const textY = 5 + headerLogoH + 4;
        doc.setFontSize(14);
        doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
        doc.setFont(FONT_HELCH, 'bold');
        doc.text(`${clubInfo.clubName || 'SRAC'} \u2014 ${targetYear} Events Calendar`, pageWidth / 2, textY, { align: 'center' });

        doc.setFontSize(10);
        doc.setFont(FONT_HELCH, 'normal');
        doc.setTextColor(100, 116, 139);
        doc.text(`Annual Overview \u2014 ${yearEvents.length} Event${yearEvents.length !== 1 ? 's' : ''}`,
            pageWidth / 2, textY + 6, { align: 'center' });

        topMargin = textY + 11;
    } else {
        doc.setFontSize(18);
        doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
        doc.setFont(FONT_HELCH, 'bold');
        doc.text(`${targetYear} Events Calendar`, pageWidth / 2, 20, { align: 'center' });

        doc.setFontSize(10);
        doc.setFont(FONT_HELCH, 'normal');
        doc.setTextColor(100, 116, 139);
        doc.text(`Annual Overview \u2014 ${yearEvents.length} Event${yearEvents.length !== 1 ? 's' : ''}`,
            pageWidth / 2, 28, { align: 'center' });

        topMargin = 33;
    }

    // Grid
    const footerH = 16 * scale;
    const contentH = pageHeight - topMargin - footerH - 5;
    const boxGap = 4;
    const boxWidth = (pageWidth - margin * 2 - boxGap * 2) / 3;
    const boxHeight = (contentH - boxGap * 3) / 4;
    const headerH = 7;

    let currentMonth = 0;

    for (let row = 0; row < 4; row++) {
        for (let col = 0; col < 3; col++) {
            if (currentMonth >= 12) break;

            const x = margin + col * (boxWidth + boxGap);
            const y = topMargin + row * (boxHeight + boxGap);

            // Month Header bar
            doc.setFillColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2]);
            doc.rect(x, y, boxWidth, headerH, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(10);
            doc.setFont(FONT_HELCH, 'bold');
            doc.text(`${MONTH_NAMES[currentMonth]}`, x + boxWidth / 2, y + 5, { align: 'center' });

            // Box border
            doc.setDrawColor(226, 232, 240);
            doc.rect(x, y, boxWidth, boxHeight);

            const monthKey = `${targetYear}-${String(currentMonth + 1).padStart(2, '0')}`;
            const monthEvents = eventsByMonth[monthKey] || [];

            const contentTop = y + headerH + 1.5;
            const contentBottom = y + boxHeight - 1.5;
            const availH = contentBottom - contentTop;

            if (monthEvents.length > 0) {
                // Calculate how many events fit
                // Each event: title line + optional detail line
                const titleLineH = 4.5;
                const detailLineH = 3.5;
                const dotSize = 1;
                const textInset = x + 2;
                const textMaxW = boxWidth - 4;

                // Determine max events that fit
                let testY = 0;
                let maxItems = 0;
                for (const ev of monthEvents) {
                    let evH = titleLineH;
                    const detail = buildDetail(ev);
                    if (detail) evH += detailLineH;
                    if (testY + evH > availH - 3) break; // reserve space for "+N more"
                    testY += evH;
                    maxItems++;
                }
                // If all fit without overflow, no need for "+N more" reserve
                if (maxItems === monthEvents.length) {
                    testY = 0;
                    maxItems = 0;
                    for (const ev of monthEvents) {
                        let evH = titleLineH;
                        const detail = buildDetail(ev);
                        if (detail) evH += detailLineH;
                        if (testY + evH > availH) break;
                        testY += evH;
                        maxItems++;
                    }
                }

                let eventY = contentTop;

                monthEvents.slice(0, maxItems).forEach(ev => {
                    // Color dot
                    const color = typeColorMap[ev.typeId] || [150, 150, 150];
                    doc.setFillColor(color[0], color[1], color[2]);
                    doc.circle(textInset + dotSize, eventY + 2, dotSize, 'F');

                    // Day + title
                    const day = toAdHocPacificDate(ev.date).getUTCDate();
                    doc.setFont(FONT_HELCH, 'bold');
                    doc.setFontSize(8.5);
                    doc.setTextColor(30, 30, 30);
                    const titleStr = `${day} - ${ev.title}`;
                    const truncTitle = truncateText(doc, titleStr, textMaxW - dotSize * 3 - 1);
                    doc.text(truncTitle, textInset + dotSize * 3 + 1, eventY + 3);

                    // Time on right
                    if (ev.time) {
                        doc.setFont(FONT_HELCH, 'normal');
                        doc.setFontSize(7);
                        doc.setTextColor(100, 100, 100);
                        doc.text(ev.time, x + boxWidth - 2, eventY + 3, { align: 'right' });
                    }

                    eventY += titleLineH;

                    // Detail line (location / description / CD)
                    const detail = buildDetail(ev);
                    if (detail) {
                        doc.setFont(FONT_HELCH, 'normal');
                        doc.setFontSize(7);
                        doc.setTextColor(140, 140, 140);
                        const truncDetail = truncateText(doc, detail, textMaxW - 2);
                        doc.text(truncDetail, textInset + 2, eventY + 1.8);
                        eventY += detailLineH;
                    }
                });

                if (monthEvents.length > maxItems) {
                    doc.setFontSize(7);
                    doc.setTextColor(100, 116, 139);
                    doc.setFont(FONT_HELCH, 'normal');
                    doc.text(`+${monthEvents.length - maxItems} more\u2026`, textInset + 2, eventY + 2.5);
                }
            } else {
                doc.setTextColor(180, 180, 180);
                doc.setFont(FONT_HELCH, 'italic');
                doc.setFontSize(8);
                doc.text('No events', x + boxWidth / 2, contentTop + availH / 2, { align: 'center' });
                doc.setFont(FONT_HELCH, 'normal');
            }

            currentMonth++;
        }
    }

    // Footer
    const footerY = pageHeight - footerH;

    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.1);
    doc.line(margin, footerY, pageWidth - margin, footerY);

    if (logoImg) {
        const targetH = 12 * scale;
        const offX = (targetH - logoDrawW) / 2;
        const offY = (targetH - logoDrawH) / 2;
        doc.addImage(logoImg, 'PNG', margin + offX, footerY + 2 + offY, logoDrawW, logoDrawH);
        doc.setFontSize(10 * scale);
        doc.setTextColor(100, 100, 100);
        doc.setFont(FONT_HELCH, 'bold');
        doc.text(clubInfo.clubName || 'SRAC', margin + targetH + 5, footerY + (targetH / 2) + 2);
    } else {
        doc.setFontSize(10 * scale);
        doc.setTextColor(100, 100, 100);
        doc.setFont(FONT_HELCH, 'normal');
        doc.text(clubInfo.clubName || 'Snohomish Radio Aero Club', margin, footerY + 8);
    }

    if (clubInfo?.clubWebsite) {
        doc.setFontSize(9 * scale);
        doc.setTextColor(100, 100, 100);
        doc.setFont(FONT_HELCH, 'normal');
        const userUrl = clubInfo.clubWebsite.replace(/^https?:\/\//, '');
        doc.text(userUrl, pageWidth - margin, footerY + 8, { align: 'right' });
    }

    doc.save(`srac-events-annual-${targetYear}.pdf`);
}

function buildDetail(ev: Event): string {
    const parts: string[] = [];
    if (ev.location) parts.push(ev.location);
    if (ev.description) parts.push(ev.description);
    if (ev.cd) parts.push(`CD: ${ev.cd}`);
    return parts.join(' \u2014 ');
}
